import React, { useEffect, useState } from 'react';
import { Plus, Save, Loader2, CheckCircle2, Edit3, X } from 'lucide-react';
import { api } from '../../lib/api';
import { Field, TextInput, TextArea, NumberInput, Select, Button } from '../../components/admin/FormControls';

const ICON_OPTIONS = [
  'Building2','Coins','Globe2','ShieldCheck','Scale','GraduationCap','HeartPulse',
  'HardHat','Anchor','Briefcase','Trophy','Users','Leaf','LifeBuoy','Ship','Wheat','Heart'
].map((v) => ({ value: v, label: v }));

const makeAdminCrud = ({ endpoint, label, emptyItem, fields }) => () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(endpoint);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2200); };

  const startNew = () => setEditing({ ...emptyItem, order: (items[items.length - 1]?.order || 0) + 1 });
  const startEdit = (n) => setEditing({ ...n });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...rest } = editing;
        await api.put(`${endpoint}/${id}`, rest);
      } else {
        await api.post(endpoint, editing);
      }
      flash('Saved');
      setEditing(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.name}"?`)) return;
    await api.delete(`${endpoint}/${n.id}`);
    flash('Deleted');
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#0B2545]">{label}</h1>
          <p className="mt-1 text-[13.5px] text-[#0B2545]/65">Manage {label.toLowerCase()} shown on the public site.</p>
        </div>
        <Button variant="gold" onClick={startNew}><Plus size={14} /> Add</Button>
      </div>

      {msg && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}

      {loading ? (
        <div className="text-[#0B2545]/60"><Loader2 className="inline animate-spin" size={14} /> Loading...</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[#E6DDC8] bg-white">
          <table className="w-full text-left text-[13.5px]">
            <thead className="bg-[#FAF6EC] text-[12px] uppercase tracking-wide text-[#0B2545]/65">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Icon</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6DDC8]">
              {items.map((n) => (
                <tr key={n.id} className="text-[#0B2545]">
                  <td className="px-4 py-3">{n.order}</td>
                  <td className="px-4 py-3 font-medium">{n.name}</td>
                  <td className="px-4 py-3 text-[#0B2545]/65">{n.slug}</td>
                  <td className="px-4 py-3 text-[#C9A86A]">{n.icon}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" onClick={() => startEdit(n)}><Edit3 size={13} /> Edit</Button>
                    <button onClick={() => remove(n)} className="ml-2 text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#0B2545]/55">Nothing here yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-xl rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#E6DDC8] px-6 py-4">
              <h3 className="font-serif text-lg font-bold text-[#0B2545]">
                {editing.id ? `Edit ${label.toLowerCase()}` : `New ${label.toLowerCase()}`}
              </h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-4 p-6">
              <Field label="Name"><TextInput value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} /></Field>
              <Field label="Slug" hint="URL-safe identifier, e.g. health"><TextInput value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} /></Field>
              <Field label="Icon"><Select value={editing.icon} onChange={(v) => setEditing({ ...editing, icon: v })} options={ICON_OPTIONS} /></Field>
              <Field label="Description"><TextArea rows={4} value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} /></Field>
              <Field label="Order"><NumberInput value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} /></Field>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#E6DDC8] px-6 py-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminMinistries = makeAdminCrud({
  endpoint: '/ministries',
  label: 'Ministries',
  emptyItem: { name: '', slug: '', icon: 'Building2', description: '', order: 0 }
});

export const AdminInvestment = makeAdminCrud({
  endpoint: '/investment-sectors',
  label: 'Investment Sectors',
  emptyItem: { name: '', slug: '', icon: 'Ship', description: '', order: 0 }
});
